'use client';

import {v7 as uuidv7} from 'uuid';
import dynamic from 'next/dynamic';
import {useState} from 'react';
import {store, UserFlowStage, useUserFlow} from '@/lib/UserFlow';
import logger from '@/lib/logger';

const NFCReader = dynamic(
    () => import('@/components/NFC/NFCReader').then(mod => mod.NFCReader),
    { ssr: false }
);

export default function NFCArea() {

    const [lastTag, setLastTag] = useState<{ tagUid: string; timestamp: Date; userUid?: string } | null>(null);
    const { stage, user } = useUserFlow();

    async function handleTagRead(tagEvent: { tagUid: string; timestamp: Date }): Promise<void> {
        if (stage !== UserFlowStage.Idle) return;

        // Ignore duplicate tag read for 7 seconds
        if (lastTag?.tagUid === tagEvent.tagUid && (Date.now() - lastTag.timestamp.getTime()) < 7000) {
            logger.warn('NFC tag read ignored (duplicate within 7 seconds)', { tagUid: tagEvent.tagUid });
            return;
        }

        setLastTag(tagEvent);

        // Generate a new UUID v7 for the user
        const newUserUid = uuidv7();

        // Update the lastTag state with the new userUid
        setLastTag({ ...tagEvent, userUid: newUserUid });

        // Start session and navigate to next stage
        store.trigger.startSession({
            user: {
                userUid: newUserUid,
                nfcTagUid: tagEvent.tagUid
            }
        });

        console.log(`Stored NFC tag ${tagEvent.tagUid} for user ${newUserUid} - will associate after form completion`);
    }

    async function handleError(errorCode: string, error?: Error): Promise<void> {
        logger.error('NFC reading error occurred', { errorCode, error });
    }

    return (
        <>
            <NFCReader onTagRead={handleTagRead} onError={handleError} />

            {/* Debug box for NFC tags */}
            {lastTag && process.env.NODE_ENV === 'development' && (
                <div className="absolute bottom-4 left-4 bg-phaethon-text/90 text-phaethon-bg px-3 py-2 rounded text-xs font-mono">
                    <div>Last Tag UID: {lastTag.tagUid}</div>
                    <div>Last Tag Time: {lastTag.timestamp.toLocaleTimeString()}</div>
                    {user.userUid && <div>Current User UID: {user.userUid}</div>}
                </div>
            )}

        </>
    );
}
